import React, { useState, useEffect, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
  SelectChangeEvent,
  IconButton,
  InputAdornment,
  Slide,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  alpha
} from '@mui/material';
import { 
  Receipt as ReceiptIcon, 
  Save as SaveIcon, 
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Store as StoreIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  AttachFile as AttachFileIcon,
  AccountBalance as AccountBalanceIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import { 
  Expense, 
  getExpenseById, 
  createExpense, 
  updateExpense, 
  deleteExpense 
} from '../firebase/expenseServices';
import { getExpenseGLAccounts, getBankGLAccounts } from '../firebase/glTransactionServices';
import GLTransactionPreview from '../components/GLTransactionPreview';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { format } from 'date-fns';

// Default categories as fallback
const defaultCategories = [
  'Rent',
  'Supplies',
  'Payroll',
  'Software',
  'Utilities',
  'Marketing',
  'Insurance',
  'Maintenance',
  'Training',
  'Equipment',
  'Office Supplies',
  'Travel',
  'Meals',
  'Taxes',
  'Other'
];

// Payment methods will be loaded from bank accounts in the 1100000 series

// Status options
const statuses = [
  'pending',
  'paid',
  'cancelled'
];

interface FormErrors {
  title?: string;
  amount?: string;
  category?: string;
  method?: string;
  date?: string;
}

interface ExpenseFormProps {
  open: boolean;
  onClose: () => void;
  expenseId?: string;
  onSave?: () => void;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ExpenseForm: React.FC<ExpenseFormProps> = ({ open, onClose, expenseId, onSave }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [expense, setExpense] = useState<Partial<Expense>>({
    title: '',
    amount: 0,
    category: '',
    method: '',
    status: 'pending',
    date: new Date(),
    description: '',
    vendor: '',
    vendorName: '',
    vendorContact: '',
    receiptUrl: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [fileUpload, setFileUpload] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [categories, setCategories] = useState<string[]>(defaultCategories);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [showGLPreview, setShowGLPreview] = useState(false);
  
  // Determine if we're in edit mode
  const isEditMode = Boolean(expenseId);
  
  useEffect(() => {
    const loadGLAccounts = async () => {
      try {
        // Load expense categories from GL accounts in 7000000 series
        const glAccounts = await getExpenseGLAccounts();
        // Extract account names from GL accounts to use as categories
        const categoryNames = glAccounts.map(account => account.accountName);
        if (categoryNames.length > 0) {
          setCategories(categoryNames);
        }
        
        // Load bank accounts from GL accounts in 1100000 series to use as payment methods
        const bankAccounts = await getBankGLAccounts();
        // Extract account names from bank accounts to use as payment methods
        const bankNames = bankAccounts.map(account => account.accountName);
        if (bankNames.length > 0) {
          setPaymentMethods(bankNames);
          // Set the initial payment method to the first bank account
          if (!isEditMode) {
            setExpense(prev => ({
              ...prev,
              method: bankNames[0]
            }));
          }
        } else {
          // Fallback if no bank accounts are found
          const fallbackMethods = ['MAYBANK', 'CIMB BANK', 'Cash'];
          setPaymentMethods(fallbackMethods);
          // Set the initial payment method to the first fallback method
          if (!isEditMode) {
            setExpense(prev => ({
              ...prev,
              method: fallbackMethods[0]
            }));
          }
        }
      } catch (error) {
        console.error('Error loading GL accounts:', error);
        // Fall back to default categories if there's an error
        setCategories(defaultCategories);
        setPaymentMethods(['MAYBANK', 'CIMB BANK', 'Cash']);
      }
    };
    
    if (open) {
      loadGLAccounts();
    }
  }, [open]);

  useEffect(() => {
    if (isEditMode && expenseId) {
      setLoading(true);
      getExpenseById(expenseId)
        .then((data) => {
          if (data) {
            setExpense(data);
          } else {
            onClose();
          }
        })
        .catch((error) => {
          console.error('Error fetching expense:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Reset form when opening for a new expense
      setExpense({
        title: '',
        amount: 0,
        category: '',
        method: 'Bank Transfer',
        status: 'pending',
        date: new Date(),
        description: '',
        vendor: '',
        vendorContact: '',
        receiptUrl: ''
      });
      setErrors({});
    }
  }, [expenseId, isEditMode, onClose, open]);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Handle amount as number
    if (name === 'amount') {
      setExpense(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }));
    } else {
      setExpense(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setExpense(prev => ({
        ...prev,
        date
      }));
      
      if (errors.date) {
        setErrors(prev => ({
          ...prev,
          date: undefined
        }));
      }
    }
  };

  // Validate form
  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};
    
    if (!expense.title) {
      newErrors.title = 'Title is required';
    }
    
    if (!expense.amount || expense.amount <= 0) {
      newErrors.amount = 'Valid amount is required';
    }
    
    if (!expense.category) {
      newErrors.category = 'Category is required';
    }
    
    if (!expense.method) {
      newErrors.method = 'Payment method is required';
    }
    
    if (!expense.date) {
      newErrors.date = 'Date is required';
    }
    
    return newErrors;
  };

  // Upload file to Firebase Storage
  const uploadFile = async (file: File): Promise<string> => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    
    // Create a storage reference with proper path: expense/year/month/filename
    const storageRef = ref(storage, `expenses/${year}/${month}/${Date.now()}_${file.name}`);
    
    // Upload the file
    await uploadBytes(storageRef, file);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setSaving(true);
    
    try {
      // Upload file if selected
      let receiptUrl = expense.receiptUrl;
      if (fileUpload) {
        setUploadProgress(true);
        receiptUrl = await uploadFile(fileUpload);
        setUploadProgress(false);
      }
      
      if (isEditMode && expenseId) {
        await updateExpense(expenseId, {
          ...expense,
          receiptUrl,
          updatedAt: new Date()
        });
        enqueueSnackbar('Expense updated successfully', { variant: 'success' });
      } else {
        // Ensure all required fields have non-undefined values
        const newExpense: Omit<Expense, 'id'> = {
          title: expense.title || '',
          amount: expense.amount || 0,
          category: expense.category || '',
          method: expense.method || 'Bank Transfer',
          status: expense.status || 'pending',
          date: expense.date || new Date(),
          description: expense.description || '',
          vendor: expense.vendor,
          vendorContact: expense.vendorContact,
          receiptUrl,
          createdBy: currentUser?.uid,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        await createExpense(newExpense);
        enqueueSnackbar('Expense created successfully', { variant: 'success' });
      }
      
      if (onSave) {
        onSave();
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving expense:', error);
      enqueueSnackbar('Failed to save expense', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle expense deletion
  const handleDelete = async () => {
    if (!expenseId) return;
    
    setSaving(true);
    
    try {
      await deleteExpense(expenseId);
      enqueueSnackbar('Expense deleted successfully', { variant: 'success' });
      
      if (onSave) {
        onSave();
      }
      
      onClose();
    } catch (error) {
      console.error('Error deleting expense:', error);
      enqueueSnackbar('Failed to delete expense', { variant: 'error' });
    } finally {
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  // Delete confirmation dialog
  const DeleteConfirmDialog = () => (
    <Dialog
      open={deleteConfirm}
      onClose={() => setDeleteConfirm(false)}
    >
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete this expense? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteConfirm(false)} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleDelete} 
          color="error" 
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {saving ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            width: { xs: '100%', sm: '90%', md: '80%' },
            maxWidth: '900px'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6">
              {isEditMode ? 'Edit Expense' : 'New Expense'}
            </Typography>
          </Box>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={onClose} 
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form id="expense-form" onSubmit={handleSubmit}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter the details of your expense. Click submit when you're done.
              </Typography>
              
              <Stack spacing={3}>
                {/* Title, Amount and Date */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Expense Title
                  </Typography>
                  <TextField
                    fullWidth
                    name="title"
                    value={expense.title || ''}
                    onChange={handleChange}
                    error={Boolean(errors.title)}
                    helperText={errors.title}
                    required
                    placeholder="Enter expense title"
                  />
                </Box>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Amount
                    </Typography>
                    <TextField
                      fullWidth
                      name="amount"
                      type="number"
                      value={expense.amount || ''}
                      onChange={handleChange}
                      error={Boolean(errors.amount)}
                      helperText={errors.amount}
                      required
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            $
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Date
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={expense.date instanceof Date ? expense.date : null}
                        onChange={handleDateChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: Boolean(errors.date),
                            helperText: errors.date,
                            InputProps: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CalendarIcon color="action" fontSize="small" />
                                </InputAdornment>
                              )
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Stack>
                
                {/* Category and Payment Method */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Category
                    </Typography>
                    <FormControl fullWidth error={Boolean(errors.category)}>
                      <Select
                        name="category"
                        value={expense.category || ''}
                        onChange={(e: SelectChangeEvent) => {
                          setExpense(prev => ({
                            ...prev,
                            category: e.target.value
                          }));
                          if (errors.category) {
                            setErrors(prev => ({
                              ...prev,
                              category: undefined
                            }));
                          }
                        }}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <Typography color="text.secondary">Select a category</Typography>;
                          }
                          return selected;
                        }}
                      >
                        {categories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
                    </FormControl>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Payment Method
                    </Typography>
                    <FormControl fullWidth error={Boolean(errors.method)}>
                      <Select
                        name="method"
                        value={expense.method || ''}
                        onChange={(e: SelectChangeEvent) => {
                          setExpense(prev => ({
                            ...prev,
                            method: e.target.value
                          }));
                          if (errors.method) {
                            setErrors(prev => ({
                              ...prev,
                              method: undefined
                            }));
                          }
                        }}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <Typography color="text.secondary">Select payment method</Typography>;
                          }
                          return selected;
                        }}
                      >
                        {paymentMethods.map((method) => (
                          <MenuItem key={method} value={method}>{method}</MenuItem>
                        ))}
                      </Select>
                      {errors.method && <FormHelperText>{errors.method}</FormHelperText>}
                    </FormControl>
                  </Box>
                </Stack>
                
                {/* Description */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Description
                  </Typography>
                  <TextField
                    fullWidth
                    name="description"
                    value={expense.description || ''}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Brief description of the expense"
                  />
                </Box>
                
                {/* Status and Vendor */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Status
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        name="status"
                        value={expense.status || 'pending'}
                        onChange={(e: SelectChangeEvent) => {
                          setExpense(prev => ({
                            ...prev,
                            status: e.target.value
                          }));
                        }}
                      >
                        {statuses.map((status) => (
                          <MenuItem key={status} value={status}>
                            <Chip 
                              size="small" 
                              label={status} 
                              color={
                                status === 'paid' ? 'success' : 
                                status === 'pending' ? 'warning' : 
                                'error'
                              }
                              sx={{ textTransform: 'capitalize' }} 
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Vendor
                    </Typography>
                    <TextField
                      fullWidth
                      name="vendor"
                      value={expense.vendor || ''}
                      onChange={handleChange}
                      placeholder="Enter vendor name"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <StoreIcon color="action" fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                </Stack>
                
                {/* Vendor Contact */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Vendor Contact
                  </Typography>
                  <TextField
                    fullWidth
                    name="vendorContact"
                    value={expense.vendorContact || ''}
                    onChange={handleChange}
                    placeholder="Enter vendor contact information"
                  />
                </Box>
                
                {/* Receipt Upload */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Receipt
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      {fileUpload ? (
                        <TextField
                          fullWidth
                          value={fileUpload.name}
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachFileIcon color="primary" fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton 
                                  onClick={() => setFileUpload(null)}
                                  size="small"
                                  color="default"
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      ) : expense.receiptUrl ? (
                        <TextField
                          fullWidth
                          value="Receipt uploaded"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ReceiptIcon color="success" fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  onClick={() => window.open(expense.receiptUrl, '_blank')}
                                  size="small"
                                  variant="outlined"
                                >
                                  View
                                </Button>
                              </InputAdornment>
                            )
                          }}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          placeholder="No file chosen"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ReceiptIcon color="action" fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      disabled={uploadProgress}
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setFileUpload(e.target.files[0]);
                          }
                        }}
                      />
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Upload a photo or PDF of your receipt (optional)
                  </Typography>
                </Box>
                
                {/* GL Transaction Preview Dialog is now separate */}
              </Stack>
            </form>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          
          {Number(expense.amount || 0) > 0 && expense.category && expense.method && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowGLPreview(!showGLPreview)}
              disabled={saving}
              startIcon={<AccountBalanceIcon />}
              sx={{ mr: 1 }}
            >
              {showGLPreview ? 'Hide Entries' : 'View Entries'}
            </Button>
          )}
          
          <Button
            type="submit"
            form="expense-form"
            variant="contained"
            color="primary"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Submit Expense'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete confirmation dialog */}
      {deleteConfirm && <DeleteConfirmDialog />}

      {/* GL Transaction Preview Dialog */}
      <Dialog
        open={showGLPreview}
        onClose={() => setShowGLPreview(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 3,
          px: 3,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccountBalanceIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              GL Transaction Preview
            </Typography>
          </Box>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={() => setShowGLPreview(false)} 
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 4,
            pb: 2,
            px: 1,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <ReceiptIcon color="primary" fontSize="medium" />
            <Typography variant="h6" color="primary" sx={{ fontWeight: 500 }}>
              {expense.title || 'New Expense'}
            </Typography>
            <Chip 
              label="Preview" 
              size="small" 
              color="secondary" 
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </Box>
          
          {Number(expense.amount || 0) > 0 && expense.category && expense.method ? (
            <GLTransactionPreview
              expenseAmount={Number(expense.amount)}
              expenseCategory={expense.category}
              paymentMethod={expense.method}
            />
          ) : (
            <Box sx={{ 
              p: 4, 
              textAlign: 'center', 
              backgroundColor: alpha(theme.palette.warning.light, 0.1),
              borderRadius: 1
            }}>
              <InfoIcon color="warning" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="subtitle1">
                Please fill in amount, category and payment method to see the GL entries.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.background.default, 0.5)
        }}>
          <Button 
            onClick={() => setShowGLPreview(false)} 
            variant="contained" 
            color="primary"
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpenseForm;
